'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
}

interface FAQSectionProps {
  className?: string;
}

const FAQSection = ({ className = '' }: FAQSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [openFAQ, setOpenFAQ] = useState<string | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const faqs: FAQ[] = [
    {
      id: '1',
      question: 'What is your typical response time for inquiries?',
      answer: 'We guarantee a response within 24 hours for all standard inquiries during business days. Urgent requests marked as high priority typically receive a response within 4 hours. For immediate assistance, we recommend using our live chat feature or calling our direct line.',
      category: 'General'
    },
    {
      id: '2',
      question: 'How long does a typical consultation last?',
      answer: 'Consultation duration varies based on the service type. Marketing strategy sessions typically last 60 minutes, AI implementation consultations run 90 minutes, and comprehensive digital transformation discussions can take up to 2 hours. We ensure adequate time to understand your needs and provide actionable insights.',
      category: 'Consultation'
    },
    {
      id: '3',
      question: 'Do you offer virtual consultations?',
      answer: 'Yes! We offer flexible consultation options including in-person meetings at our offices, video calls via Zoom or Microsoft Teams, and phone consultations. Virtual meetings have proven highly effective and allow us to serve clients globally while maintaining the same level of personalized attention.',
      category: 'Consultation'
    },
    {
      id: '4',
      question: 'What information should I prepare before our consultation?',
      answer: 'To maximize our consultation time, we recommend preparing: current business challenges you\'re facing, your goals for the next 6-12 months, any existing marketing or automation tools you use, approximate budget range, and specific questions you\'d like addressed. Don\'t worry if you don\'t have everything - we\'ll guide you through the discovery process.',
      category: 'Consultation'
    },
    {
      id: '5',
      question: 'Is there a cost for the initial consultation?',
      answer: 'Your first strategic consultation is complimentary. This 60-minute session allows us to understand your business needs, discuss potential solutions, and determine if we\'re the right fit for your project. There\'s no obligation to proceed, and you\'ll receive valuable insights regardless.',
      category: 'Pricing'
    },
    {
      id: '6',
      question: 'What happens after I submit a contact form?',
      answer: 'After submission, you\'ll receive an immediate confirmation email. Within 24 hours, a member of our team will review your inquiry and reach out via your preferred contact method to schedule a consultation or answer your questions. For urgent matters, we prioritize response within 4 hours.',
      category: 'Process'
    }
  ];

  const toggleFAQ = (id: string) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  if (!isHydrated) {
    return (
      <section className={`py-16 bg-background ${className}`}>
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-3/4 mx-auto"></div>
            <div className="h-4 bg-muted rounded w-1/2 mx-auto"></div>
            <div className="space-y-4 mt-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-16 bg-card rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`py-16 bg-background ${className}`}>
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-muted-foreground">
            Quick answers to common questions about our consultation process and services
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq) => (
            <div
              key={faq.id}
              className="bg-card rounded-lg border border-border overflow-hidden transition-all duration-300 hover:shadow-subtle"
            >
              <button
                onClick={() => toggleFAQ(faq.id)}
                className="w-full px-6 py-4 flex items-center justify-between text-left transition-colors duration-300 hover:bg-muted/50"
              >
                <div className="flex-1 pr-4">
                  <span className="text-xs font-semibold text-primary uppercase tracking-wider">
                    {faq.category}
                  </span>
                  <h3 className="text-lg font-semibold text-foreground mt-1">
                    {faq.question}
                  </h3>
                </div>
                <Icon
                  name="ChevronDownIcon"
                  size={24}
                  className={`text-primary flex-shrink-0 transition-transform duration-300 ${
                    openFAQ === faq.id ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {openFAQ === faq.id && (
                <div className="px-6 pb-4 pt-2">
                  <p className="text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 bg-primary/5 rounded-lg p-6 border border-primary/20">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
              <Icon name="QuestionMarkCircleIcon" size={24} className="text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Still have questions?
              </h3>
              <p className="text-muted-foreground mb-4">
                Can't find the answer you're looking for? Our team is here to help with any additional questions you may have.
              </p>
              <button className="px-6 py-2 bg-primary text-primary-foreground rounded-md font-semibold hover:bg-primary/90 transition-colors duration-300">
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;